import { Injectable } from '@angular/core';
import { EnvironmentConfig } from '../bootstrap/environment-config';

export enum Level { OFF, ERROR, WARN, INFO, DEBUG, TRACE }

/**
 * Der LoggingService stellt verschiedene Methoden zum Loggen auf der Browserkonsole bereit.
 * Die Meldungen werden nur auf die Konsole geschrieben, wenn das eingestellte Log-Level größer
 * oder gleich dem Level der Log-Methode ist, z.B. werden DEBUG-Meldungen nur auf die Konsole
 * geschrieben, wenn das Log-Level DEBUG oder TRACE ist. Zum Einstellen des Log-Levels kann auf
 * der Konsole logger.level=$LEVEL$ aufgerufen werden, wobei $LEVEL$ eins der einstellbaren
 * Log-Level ist.
 *
 * @author Valentin Kennke (vake@mms-dresden.de)(valentin.kennke@t-systems.com)
 * @author Stefan Ocke (stoc@mms-dresden.de)(stefan.ocke@t-systems.com)
 */
@Injectable()
export class LoggingService {

    /**
     * Setzt das Log-Level. Der Wert muss einem der Werte { OFF, ERROR, WARN, INFO, DEBUG, TRACE } entsprechen oder
     * deren numerischer Repräsentation (0 bis 5).
     *
     * @param level das neue Log-Level
     */
    set level(level: Level) {
        if (Level[level] !== 'undefined') {
            this._level = level;
            localStorage.setItem('portal.logger.level', this.str(level));
        } else {
            // eslint-disable-next-line no-console
            console.warn('Das Level muss einer der folgenden Werte sein: ' + JSON.stringify(Object.keys(Level)));
        }
    }

    private static INSTANCE: LoggingService;
    private _level: Level;
    private legacyMode: boolean = true;

    constructor(envConfig?: EnvironmentConfig) {
        (<any> window)['logger'] = this;
        Object.keys(Level).filter(level => isNaN(+level)).forEach(level => (<any> window)[level] = level);
        const levelFromStorage: Level = Level[localStorage.getItem('portal.logger.level')];
        // Test ob levelFromStorage null oder undefined ist oder levelFromStorage keiner der vorgegebenen Log-Level entspricht
        // In diesem Fall wird das Level auf einen vorgegebenen, umgebungsabhängigen Wert gesetzt
        if (!levelFromStorage || !Level[levelFromStorage]) {
            if (envConfig && envConfig.isDev()) {
                this._level = Level.TRACE;
            } else {
                this._level = Level.WARN;
            }
            this.debug('Das im localStorage gespeicherte Log-Level entspricht keinem der folgenden Werte: '
                + JSON.stringify(Object.keys(Level))
                + '. Daher wurde das Level auf ' + this.str(this._level) + ' gesetzt.');
        } else {
            this._level = levelFromStorage;
        }
    }

    /**
     * Schreibt eine Fehlermeldung (rot) inklusive Stacktrace auf die Konsole. Wird immer angezeigt, außer
     * das Log-Level = OFF.
     *
     * @param msg die anzuzeigende Fehlermeldung
     */
    public error(msg: any) {
        // eslint-disable-next-line no-console
        this.log(Level.ERROR, console.error, msg);
    }

    /**
     * Schreibt eine Warnmeldung (gelb) auf die Konsole. Wird immer angezeigt, außer das Log-Level = ERROR oder OFF.
     *
     * @param msg die anzuzeigende Warnmeldung
     */
    public warn(msg: any) {
        // eslint-disable-next-line no-console
        this.log(Level.WARN, console.warn, msg);
    }

    /**
     * Schreibt eine Infomeldung auf die Konsole. Wird bei Log-Level INFO, DEBUG und TRACE angezeigt.
     *
     * @param msg die anzuzeigende Infomeldung
     */
    public info(msg: any) {
        // eslint-disable-next-line no-console
        this.log(Level.INFO, console.info, msg);
    }

    /**
     * Schreibt eine Debugmeldung auf die Konsole. Wird bei Log-Level DEBUG und TRACE angezeigt.
     *
     * @param msg die anzuzeigende Debugmeldung
     */
    public debug(msg: any) {
        // eslint-disable-next-line no-console
        this.log(Level.DEBUG, console.log, msg);
    }

    /**
     * Schreibt eine Debugmeldung inklusive Stacktrace auf die Konsole. Wird nur bei Log-Level TRACE angezeigt.
     *
     * @param msg die anzuzeigende Meldung
     * @param printTrace (optional )soll ein Stacktrace geschrieben werden, default = false
     */
    public trace(msg: any, printTrace: boolean = false) {
        // eslint-disable-next-line no-console
        this.log(Level.TRACE, printTrace ? console.trace : console.log, msg);
    }

    /**
     * Schreibt die Javascript-Repräsentation eines Objektes auf die Konsole. Diese Methode sollte generell für Objekte
     * verwendet werden. Das Log-Level ist standardmäßig DEBUG, kann aber angepasst werden.
     *
     * @param obj das Objekt was auf die Konsole geschrieben werden soll.
     * @param level (optional) das Log-Level für diese Meldung, Standardmäßig DEBUG
     * @deprecated Bitte debug/ info/ trace etc. nutzen, die können Objekte genauso schön auf der Konsole ausgeben
     */
    public dir(obj: any, level: Level = Level.DEBUG) {
        // eslint-disable-next-line no-console
        this.log(level, console.log, obj);
    }

    /**
     * Schreibt die XML-Repräsentation eines XML/HTML Elements oder die Javascript-Repräsentation eines Objektes auf die Konsole.
     * Diese Methode (oder dir) sollte generell für XML/HTML-Elemente verwendet werden. Das Log-Level ist standardmäßig DEBUG,
     * kann aber angepasst werden.
     *
     * @param obj das Objekt was auf die Konsole geschrieben werden soll.
     * @param level (optional) das Log-Level für diese Meldung, Standardmäßig DEBUG
     */
    public dirxml(obj: any, level: Level = Level.DEBUG) {
        // eslint-disable-next-line no-console
        this.log(level, console.dirxml, obj);
    }

    /**
     * Schreibt die Nachricht auf die Konsole, wenn der übergebene boolsche Ausdruck false ist und das
     * eingestellte Log-Level größer oder gleich dem übergebenen Log-Level ist.
     *
     * @param test der boolsche Ausdruck
     * @param msg die anzuzeigende Nachricht
     * @param level (optional) das Log-Level für diese Meldung, Standardmäßig DEBUG
     */
    public assert(test: boolean, msg: any, level: Level = Level.DEBUG) {
        if (!test) {
            // eslint-disable-next-line no-console
            this.log(level, console.log, msg);
        }
    }

    private log(level: Level, logFunction: (message?: any, ...optionalParams: any[]) => void, msg: object | object[]) {
        if (this.num(level) <= this.num(this._level)) {
            const [metaData, ...msgObjects] = this.legacyMode || !Array.isArray(msg) ? ['', msg] : msg;
            let levelColor;
            switch (level) {
                case Level.DEBUG:
                    levelColor = 'color:LightSlateGray;';
                    break;
                case Level.INFO:
                    levelColor = 'color:LimeGreen;';
                    break;
                case Level.TRACE:
                    levelColor = 'color:MediumPurple;';
                    break;
                default:
                    levelColor = 'color:Black';
                    break;
            }

            const time = new Date();
            const dlz = ('0' + time.getDate()).slice(-2); // day leading zero
            const mlz = ('0' + (time.getMonth() + 1)).slice(-2); // month leading zero
            const ylz = time.getFullYear();
            const localeTime = time.toLocaleTimeString(); // 09:08:11 - hh:mm:ss (already with leading zeros)
            const ms = `00${time.getMilliseconds()}`.slice(-3); // ms with leading zeros
            const md = `${ylz}-${mlz}-${dlz} ${localeTime}.${ms}`
                + ' %c' + Level[level] + metaData + ' %c:';

            const messageWithOptions = [md, levelColor, 'color:Black;', ...msgObjects];
            logFunction.apply(console, messageWithOptions);
        }
    }

    private num(level: Level) {
        if (isNaN(+level)) {
            return +Level[level];
        }
        return level;
    }

    private str(level: Level) {
        if (isNaN(+level)) {
            return level.toString();
        }
        return Level[level];
    }

    /**
     * Ist der Wert auf true gesetzt wird die zu loggende Nachricht 1:1 (bzw. destrukturiert) auf die Konsole geschrieben, das ist das
     * Standardverhalten. Wird der Wert auf false gesetzt, dann wird angenommen, dass die Nachricht in dem Format
     * [Metadaten, Nachricht] übermittelt wird, d.h. die Metadaten (z.B. welche Klasse will hier loggen) werden noch farblich abgesetzt
     * vor der eigentlichen Nachricht auf die Konsole geschrieben.
     *
     * @param legacyMode true oder false
     */
    private setLegacyMode(legacyMode: boolean): LoggingService {
        this.legacyMode = legacyMode;
        return this;
    }

    /**
     * Der LoggingService muss nicht zwangsläufig injected werden sondern kann auch als normales Singleton geholt werden.
     * Der Grund wieso er bisher als injectable Service verwendet wurde ist, dass der LoggingService sich die EvironmentConfig
     * injecten lassen muss. Diese nutzt er aber nur um das initale LoggingLevel abhängig von der Umgebung (dev/ prod) zu setzen.
     * Holt man sich den LoggingService als normales Singleton über diese Methode wird das LoggingLevel initial (sofern nicht anderes
     * im LocalStorage gespeichert ist) auf WARN gesetzt.
     */
    public static getInstance(): LoggingService {
        return LoggingService.INSTANCE || (LoggingService.INSTANCE = new LoggingService().setLegacyMode(false));
    }
}
